import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DTypeRepository, DTypeStatus, NextLocator, SqlTableInfo } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { DTypeService } from '../dtype.service';
import { SqlItemComparison, SqlTableComparison } from '../dtype.types';

@Component({
    selector: 'database-table-view',
    templateUrl: 'database-table-view.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DatabaseTableViewComponent implements OnInit, OnDestroy {

    @Input() data: DTypeRepository;
    @Input() uri: string;
    @Input() dtypeLocator: NextLocator;
    @Input() hasLiveRelease: boolean;

    loading: boolean;
    sqlTablesDesign: SqlTableInfo;
    drawSqlTablesDesign: SqlTableComparison;
    drawDerivedSqlTablesDesign = new Map<string,SqlTableComparison>();
    sqlTablesLive: SqlTableInfo;
    drawSqlTablesLive: SqlTableComparison;
    drawDerivedSqlTablesLive = new Map<string,SqlTableComparison>();
    derivedListNames: string[] = [];
    showView: string;

    comparisonDone: boolean = false;

    viewMode: FormControl;

    visibleDesign: boolean = false;
    visibleLive: boolean = false;
    visibleCompare: boolean = false;

    private unsubscribeAll: Subject<void> = new Subject<void>();

    constructor(
        private dTypeService: DTypeService,
        private translocoService: TranslocoService,
    ) { }

    async ngOnInit() {
        this.loading = true;
        this.viewMode = new FormControl('');
        this.tableSetUp();

        this.viewMode.valueChanges.pipe(takeUntil(this.unsubscribeAll)).subscribe(async (value) => {
            this.loading = true;
            if (value === DTypeStatus.DESIGN || value === 'COMPARE') {
                if (!this.sqlTablesDesign) {
                    const reply = await firstValueFrom(this.dTypeService.retrieveDTypeRepositoryByUri({uri: this.uri, status: DTypeStatus.DESIGN, locator: this.dtypeLocator,
                         bindableOptions: true}));
                    this.sqlTablesDesign = reply?.sqlTable;
                    this.drawSqlTablesDesign = this.initialDrawTables(this.sqlTablesDesign, DTypeStatus.DESIGN);
                    for (const key of Object.keys(this.sqlTablesDesign.derived)) {
                        this.drawDerivedSqlTablesDesign.set(key, this.initialDrawTables(this.sqlTablesDesign.derived[key], DTypeStatus.DESIGN));
                    }
                }
            }
            if (value === DTypeStatus.LIVE || value === 'COMPARE') {
                if (!this.sqlTablesLive) {
                    const reply = await firstValueFrom(this.dTypeService.retrieveDTypeRepositoryByUri({uri: this.uri, status: DTypeStatus.LIVE, locator: this.dtypeLocator,
                        bindableOptions: true}));
                    this.sqlTablesLive = reply?.sqlTable;
                    this.drawSqlTablesLive = this.initialDrawTables(this.sqlTablesLive, DTypeStatus.LIVE);
                    for (const key of Object.keys(this.sqlTablesLive.derived)) {
                        this.drawDerivedSqlTablesLive.set(key, this.initialDrawTables(this.sqlTablesLive.derived[key], DTypeStatus.LIVE));
                    }
                }
            }
            if (value === 'COMPARE' && !this.comparisonDone) {
                this.derivedListNames = this.getDerivedList();
                this.compareTables(this.drawSqlTablesDesign, this.drawSqlTablesLive);
                for (const key of this.derivedListNames) {
                    this.compareTables(this.drawDerivedSqlTablesDesign.get(key), this.drawDerivedSqlTablesLive.get(key));
                }
                this.comparisonDone = true;
            }
            this.showView = value;
            this.loading = false;
        });

        this.viewMode.setValue(this.data?.status);
        this.loading = false;
    }

    ngOnDestroy() {
        // Unsubscribe from all subscriptions
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    private tableSetUp(): void {
        if (this.data?.status === DTypeStatus.DESIGN) {
            this.sqlTablesDesign = this.data?.sqlTable;
            this.drawSqlTablesDesign = this.initialDrawTables(this.sqlTablesDesign, DTypeStatus.DESIGN);
            for (const key of Object.keys(this.sqlTablesDesign.derived)) {
                this.drawDerivedSqlTablesDesign.set(key, this.initialDrawTables(this.sqlTablesDesign.derived[key], DTypeStatus.DESIGN));
            }
        }
        if (this.data?.status === DTypeStatus.LIVE) {
            this.sqlTablesLive = this.data?.sqlTable;
            this.drawSqlTablesLive = this.initialDrawTables(this.sqlTablesLive, DTypeStatus.LIVE);
            for (const key of Object.keys(this.sqlTablesLive.derived)) {
                this.drawDerivedSqlTablesLive.set(key, this.initialDrawTables(this.sqlTablesLive.derived[key], DTypeStatus.LIVE));
            }
        }
        if (this.sqlTablesDesign) {
            this.visibleDesign = true;
        }
        if (this.sqlTablesLive || this.hasLiveRelease) {
            this.visibleDesign = true;
            this.visibleLive = true;
            this.visibleCompare = true;
        }
    }

    private initialDrawTables(table: SqlTableInfo, _status: DTypeStatus): SqlTableComparison {
        const _tableType: SqlItemComparison = table.whyThisTable?.type ? {
            name: 'dbTables.tableType',
            value: table.whyThisTable?.type,
            isDifferent: false,
            onlyHere: false
        } : undefined;
        const _elementType: SqlItemComparison = table.whyThisTable?.facadeUri ? {
            name: 'dbTables.elementType',
            value: table?.whyThisTable?.facadeUri.split('.').slice(-1)[0],
            isDifferent: false,
            onlyHere: false,
            tooltipItem: {tooltip: table.whyThisTable?.facadeUri, show: false}
        } : undefined;
        const _tableName: SqlItemComparison = {
            name: 'dbTables.tableName',
            value: table.name ?? '<<' + this.translocoService.translate('dbTables.unassigned') + '>>',
            isDifferent: false,
            onlyHere: false,
            tooltipItem: table.name ? {tooltip: '', show: true} : undefined
        };
        const _tableSchema: SqlItemComparison = {
            name: 'dbTables.tableSchema',
            value: table.schema ?? '<<' + this.translocoService.translate('dbTables.unassigned') + '>>',
            isDifferent: false, onlyHere: false};
        const _uriName: SqlItemComparison = table.whyThisTable?.uriName ? {
            name: 'dbTables.uriName',
            value: table.whyThisTable?.uriName,
            isDifferent: false,
            onlyHere: false,
            tooltipItem: {tooltip: table.whyThisTable?.uri, show: false}
        } : undefined;
        const _linkField: SqlItemComparison = table.whyThisTable?.field?.length > 0 ? {
            name: 'dbTables.linkField',
            value: table.whyThisTable?.field.join(),
            isDifferent: false,
            onlyHere: false
        } : undefined;
        const _linkType: SqlItemComparison = table.whyThisTable?.complexName ? {
            name: 'dbTables.linkType',
            value: table.whyThisTable?.complexName,
            isDifferent: false,
            onlyHere: false,
            tooltipItem: {tooltip: table.whyThisTable?.complexUri, show: false}
        } : undefined;

        const _dn: SqlItemComparison[] = [];
        if (table.whyThisTable?.dn?.parts?.length > 0) {
            for (const item of table.whyThisTable?.dn?.parts) {
                _dn.push({name: item.name, value: item.text, isDifferent: false, onlyHere: false});
            }
        }

        const _columns: SqlItemComparison[] = [];
        if (table.columns?.length > 0) {
            for (const item of table.columns) {
                let _value: string;
                if (item.length !== -1) {
                    if (item.scale > 0) {
                        _value = item.sqlType + '(' + item.length + ',' + item.scale + ')';
                    } else {
                        _value = item.sqlType + '(' + item.length + ')';
                    }
                } else {
                    _value = item.sqlType;
                }
                const _tooltip: string =
                    'name: ' + item.name + '\n' +
                    'ordinal: ' + item.ordinal + '\n' +
                    'sqlType: ' + item.sqlType + '\n' +
                    'type: ' + item.type + '\n' +
                    'length: ' + item.length + '\n' +
                    'scale: ' + item.scale + '\n' +
                    'nullable: ' + item.nullable + '\n';
                _columns.push({name: item.name, value: _value, isDifferent: false, onlyHere: false, tooltipItem: {tooltip: _tooltip, show: false}});
            }
        }

        const _indexes: SqlItemComparison[] = [];
        if (table.indexes?.length > 0) {
            for (const _index of table.indexes) {
                let _indexValue: string = '';
                const _indexValues: string[] = [];
                let _indexName: string = '';
                for (const _column of _index.columns) {
                    if (_indexValue === '') {
                        _indexValue = _column.columnName;
                        if (_column.descending) {
                            _indexValue = _indexValue + ' DESC';
                            _indexValues.push(_indexValue);
                        } else {
                            _indexValues.push(_indexValue);
                        }
                    } else {
                        _indexValue += ', ' + _column.columnName;
                        if (_column.descending) {
                            _indexValue = _indexValue + ' DESC';
                            _indexValues.push(_indexValue);
                        } else {
                            _indexValues.push(_indexValue);
                        }
                    }
                    if (_index.primary) {
                        _indexName = 'primary';
                    } else if (_index.unique) {
                        _indexName = 'unique';
                    } else {
                        _indexName = 'index';
                    }
                }
                _indexes.push({name: _indexName, value: _indexValue, listValues: _indexValues, isDifferent: false, onlyHere: false});
            }
        }

        const drawTable: SqlTableComparison = {
            tableType: _tableType,
            tableName: _tableName,
            elementType: _elementType,
            tableSchema: _tableSchema,
            uriName: _uriName,
            linkField: _linkField,
            linkType: _linkType,
            dn: _dn,
            columns: _columns,
            indexes: _indexes,
            status: _status,
        };
        return drawTable;
    }

    private compareTables(table1: SqlTableComparison, table2: SqlTableComparison): void {
        if (!table1) {
            this.toggleAllOnlyHere(table2);
        } else if (!table2) {
            this.toggleAllOnlyHere(table1);
        } else {
            //Single value items
            const _singleKeys = ['tableType', 'elementType', 'uriName', 'linkField', 'linkType'];
            for (const _key of _singleKeys) {
                if (!table1[_key]) {
                    if (table2[_key]) {
                        table2[_key].onlyHere = true;
                    }
                } else {
                    if (!table2[_key]) {
                        table1[_key].onlyHere = true;
                    } else if (table1[_key].value !== table2[_key].value) {
                        table1[_key].isDifferent = true;
                        table2[_key].isDifferent = true;
                    }
                }
            }

            //Dn and Columns
            const _objectKeys = ['dn', 'columns'];
            for (const _key of _objectKeys) {
                //Items table1
                for (const _originItem of table1[_key]) {
                    let _exists = false;
                    let _equal = false;
                    for (const _compareItem of table2[_key]) {
                        if (_originItem.name === _compareItem.name) {
                            _exists = true;
                            if (_originItem.value === _compareItem.value) {
                                _equal = true;
                            }
                        }
                    }
                    if (!_exists) {
                        _originItem.onlyHere = true;
                    } else if (!_equal) {
                        _originItem.isDifferent = true;
                    }
                }
                //Items table2
                for (const _originItem of table2[_key]) {
                    let _exists = false;
                    let _equal = false;
                    for (const _compareItem of table1[_key]) {
                        if (_originItem.name === _compareItem.name) {
                            _exists = true;
                            if (_originItem.value === _compareItem.value) {
                                _equal = true;
                            }
                        }
                    }
                    if (!_exists) {
                        _originItem.onlyHere = true;
                    } else if (!_equal) {
                        _originItem.isDifferent = true;
                    }
                }
            }

            //Indexes
            //Items table1
            for (const _originItem of table1.indexes) {
                let _exists = false;
                let _equal = true;
                for (const _compareItem of table2.indexes) {
                    if (_originItem.name === _compareItem.name) {
                        _exists = true;
                        if (_originItem.listValues?.length !== _compareItem.listValues?.length) {
                            _originItem.isDifferent = true;
                            _compareItem.isDifferent = true;
                        } else {
                            for (const _originValue of _originItem.listValues) {
                                let _valueExists = false;
                                for (const _compareValue of _compareItem.listValues) {
                                    if (_originValue === _compareValue) {
                                        _valueExists = true;
                                    }
                                }
                                if (!_valueExists) {
                                    _equal = false;
                                }
                            }
                            if (!_equal) {
                                _originItem.isDifferent = true;
                                _compareItem.isDifferent = true;
                            }
                        }
                    }
                }
                if (!_exists) {
                    _originItem.onlyHere = true;
                }
            }
            //Items table2
            for (const _originItem of table2.indexes) {
                let _exists = false;
                for (const _compareItem of table1.indexes) {
                    if (_originItem.name === _compareItem.name) {
                        _exists = true;
                    }
                }
                if (!_exists) {
                    _originItem.onlyHere = true;
                }
            }
        }
    }

    private toggleAllOnlyHere(table: SqlTableComparison): void {
        //Single value items
        const _singleKeys = ['tableType', 'tableName', 'elementType', 'tableSchema', 'uriName', 'linkField', 'linkType'];
        for (const _key of _singleKeys) {
            if (table[_key]) {
                table[_key].onlyHere = true;
            }
        }

        //Dn and Columns
        const _objectKeys = ['dn', 'columns'];
        for (const _key of _objectKeys) {
            //Items table
            for (const _originItem of table[_key]) {
                _originItem.onlyHere = true;
            }
        }

        //Indexes
        //Items table1
        for (const _originItem of table.indexes) {
            _originItem.onlyHere = true;
        }
    }

    private getDerivedList(): string[] {
        const _list: string[] = [];
        //Common names
        for (const key of this.drawDerivedSqlTablesDesign.keys()) {
            if (this.drawDerivedSqlTablesLive.has(key)) {
                _list.push(key);
            }
        }
        //Unique names
        for (const key of this.drawDerivedSqlTablesDesign.keys()) {
            if(!_list.includes(key)) {
                _list.push(key);
            }
        }
        for (const key of this.drawDerivedSqlTablesLive.keys()) {
            if(!_list.includes(key)) {
                _list.push(key);
            }
        }
        return _list;
    }
}
export { SqlTableComparison };

